import React, { useState, useEffect, useMemo } from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Select, type SelectOption } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';

interface ModelInfo {
	id: string;
	name: string;
	provider: string;
	description?: string;
	supportsJsonMode?: boolean;
	supportsTools?: boolean;
	supportsVision?: boolean;
	supportsImageGeneration?: boolean;
}

interface SettingField {
	key: string;
	label: string;
	type: 'select' | 'slider' | 'toggle';
	options?: Array<{ value: string; label: string }>;
	min?: number;
	max?: number;
	step?: number;
	default: any;
}

interface ImageSizeOption {
	value: string;
	label: string;
}

interface ImageModelDefinition {
	id: string;
	name: string;
	provider: string;
	endpoint?: string;
	description?: string;
	supportsReferenceImages: boolean;
	maxReferenceImages?: number;
	requiresReferenceImage?: boolean;
	sizeOptions?: ImageSizeOption[];
	aspectRatioOptions?: string[];
	settings: SettingField[];
	defaultSize?: string;
	defaultAspectRatio?: string;
	costPerImage: number;
}

interface PlaygroundResponse {
	text: string;
	usage: {
		inputTokens: number;
		outputTokens: number;
	};
	provider: string;
	model: string;
}

interface ImagePlaygroundResponse {
	imageData: string;
	imageUrl?: string;
	provider: string;
	model: string;
}

type PlaygroundMode = 'text' | 'image';

export function LLMPlayground() {
	const [mode, setMode] = useState<PlaygroundMode>('text');
	const [models, setModels] = useState<ModelInfo[]>([]);
	const [imageModels, setImageModels] = useState<ImageModelDefinition[]>([]);
	const [selectedModel, setSelectedModel] = useState<string>('');
	const [prompt, setPrompt] = useState<string>('');
	const [temperature, setTemperature] = useState<number>(0.8);
	const [maxOutputTokens, setMaxOutputTokens] = useState<number>(4096);
	const [jsonMode, setJsonMode] = useState<boolean>(false);
	const [aspectRatio, setAspectRatio] = useState<string>('1:1');
	const [imageSize, setImageSize] = useState<string>('');
	const [modelSettings, setModelSettings] = useState<Record<string, any>>({});
	const [loading, setLoading] = useState(false);
	const [response, setResponse] = useState<PlaygroundResponse | null>(null);
	const [imageResponse, setImageResponse] = useState<ImagePlaygroundResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		(async () => {
			try {
				const [modelsRes, imageModelsRes] = await Promise.all([
					fetch('/api/admin/llm/models', { credentials: 'include' }),
					fetch('/api/admin/llm/image-models', { credentials: 'include' }),
				]);
				const modelsData = await modelsRes.json();
				const imageModelsData = await imageModelsRes.json();

				if (modelsData.success) {
					setModels(modelsData.data);
					const textModels = modelsData.data.filter((m: ModelInfo) => !m.supportsImageGeneration);
					if (textModels.length > 0) {
						setSelectedModel(textModels[0].id);
					}
				}
				if (imageModelsData.success) {
					setImageModels(imageModelsData.data);
				}
			} catch (e) {
				console.error('Failed to load models', e);
			}
		})();
	}, []);

	// Get the current image model definition
	const selectedImageModelDef = useMemo(
		() => imageModels.find(m => m.id === selectedModel),
		[imageModels, selectedModel]
	);

	// Update selected model and reset settings when mode changes
	useEffect(() => {
		if (mode === 'image') {
			if (imageModels.length > 0) {
				const firstModel = imageModels[0];
				setSelectedModel(firstModel.id);
				resetSettingsForModel(firstModel);
			}
		} else {
			const textModels = models.filter((m) => !m.supportsImageGeneration);
			if (textModels.length > 0) {
				setSelectedModel(textModels[0].id);
			}
		}
		setResponse(null);
		setImageResponse(null);
	}, [mode, models, imageModels]);

	function resetSettingsForModel(modelDef: ImageModelDefinition) {
		setAspectRatio(modelDef.defaultAspectRatio || '');
		setImageSize(modelDef.defaultSize || '');
		const defaults: Record<string, any> = {};
		for (const field of modelDef.settings) {
			defaults[field.key] = field.default;
		}
		setModelSettings(defaults);
	}

	function handleModelChange(modelId: string) {
		setSelectedModel(modelId);
		if (mode === 'image') {
			const modelDef = imageModels.find(m => m.id === modelId);
			if (modelDef) {
				resetSettingsForModel(modelDef);
			}
		}
	}

	const filteredModels = mode === 'image'
		? models.filter((m) => m.supportsImageGeneration)
		: models.filter((m) => !m.supportsImageGeneration);

	const modelOptions: SelectOption[] = mode === 'image'
		? imageModels.map((m) => ({
			value: m.id,
			label: `${m.name} (${m.provider})`,
		}))
		: filteredModels.map((m) => ({
			value: m.id,
			label: `${m.name} (${m.provider})`,
		}));

	const selectedModelInfo = models.find((m) => m.id === selectedModel);

	const handleSubmit = async () => {
		if (!prompt.trim() || !selectedModel) {
			setError('Please provide a prompt and select a model');
			return;
		}

		setLoading(true);
		setError(null);
		setResponse(null);
		setImageResponse(null);

		try {
			if (mode === 'image') {
				const body: Record<string, any> = {
					model: selectedModel,
					prompt,
				};
				if (selectedImageModelDef?.aspectRatioOptions && aspectRatio) {
					body.aspectRatio = aspectRatio;
				}
				if (selectedImageModelDef?.sizeOptions && imageSize) {
					body.imageSize = imageSize;
				}
				if (selectedImageModelDef?.settings.length) {
					body.modelSettings = modelSettings;
				}

				const res = await fetch('/api/admin/llm/playground/image', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify(body),
				});

				const data = await res.json();
				if (data.success) {
					setImageResponse(data.data);
				} else {
					setError(data.error?.message || 'Failed to generate image');
				}
			} else {
				const res = await fetch('/api/admin/llm/playground', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify({
						model: selectedModel,
						prompt,
						temperature,
						maxOutputTokens,
						jsonMode,
					}),
				});

				const data = await res.json();
				if (data.success) {
					setResponse(data.data);
				} else {
					setError(data.error?.message || 'Failed to generate response');
				}
			}
		} catch (e: any) {
			setError(e.message || 'An error occurred');
		} finally {
			setLoading(false);
		}
	};

	function renderSettingField(field: SettingField) {
		const value = modelSettings[field.key] ?? field.default;
		switch (field.type) {
			case 'select':
				return (
					<Select
						options={field.options?.map(o => ({ value: o.value, label: o.label })) || []}
						value={value}
						onValueChange={(v) => setModelSettings(prev => ({ ...prev, [field.key]: v }))}
					/>
				);
			case 'slider':
				return (
					<div className="flex items-center gap-3">
						<input
							type="range"
							min={field.min}
							max={field.max}
							step={field.step}
							value={value}
							onChange={(e) => setModelSettings(prev => ({ ...prev, [field.key]: Number(e.target.value) }))}
							className="flex-1"
						/>
						<span className="text-sm font-mono text-gray-600 min-w-[3rem] text-right">{value}</span>
					</div>
				);
			case 'toggle':
				return (
					<label className="flex items-center gap-2">
						<input
							type="checkbox"
							checked={!!value}
							onChange={(e) => setModelSettings(prev => ({ ...prev, [field.key]: e.target.checked }))}
							className="rounded border-gray-300"
						/>
						<span className="text-sm text-gray-600">{value ? 'On' : 'Off'}</span>
					</label>
				);
			default:
				return null;
		}
	}

	return (
		<PageContainer>
			<PageHeader
				title="LLM Playground"
				description="Test and experiment with different LLM models and configurations."
			/>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
				{/* Input Panel */}
				<Card>
					<CardHeader>
						<CardTitle>Configuration</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">Mode</label>
							<div className="flex gap-2">
								<Button
									variant={mode === 'text' ? 'primary' : 'outline'}
									onClick={() => setMode('text')}
									className="flex-1"
								>
									Text Generation
								</Button>
								<Button
									variant={mode === 'image' ? 'primary' : 'outline'}
									onClick={() => setMode('image')}
									className="flex-1"
								>
									Image Generation
								</Button>
							</div>
						</div>

						<Select
							label="Model"
							options={modelOptions}
							value={selectedModel}
							onValueChange={handleModelChange}
							placeholder="Select a model"
						/>

						{mode === 'image' && selectedImageModelDef && (
							<div className="text-sm text-gray-600 space-y-1">
								<div className="flex items-center gap-2 flex-wrap">
									<Badge variant="outline">{selectedImageModelDef.provider}</Badge>
									{selectedImageModelDef.supportsReferenceImages && (
										<Badge variant="outline">Refs: {selectedImageModelDef.maxReferenceImages || '?'}</Badge>
									)}
									<Badge variant="outline">${selectedImageModelDef.costPerImage}/img</Badge>
								</div>
								{selectedImageModelDef.description && (
									<p className="text-xs text-gray-500">{selectedImageModelDef.description}</p>
								)}
								{selectedImageModelDef.requiresReferenceImage && (
									<p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mt-1">
										This model requires a reference image (not available in playground).
									</p>
								)}
							</div>
						)}

						{mode === 'text' && selectedModelInfo && (
							<div className="text-sm text-gray-600 space-y-1">
								<div className="flex items-center gap-2 flex-wrap">
									<Badge variant="outline">{selectedModelInfo.provider}</Badge>
									{selectedModelInfo.supportsJsonMode && (
										<Badge variant="outline">JSON Mode</Badge>
									)}
									{selectedModelInfo.supportsTools && (
										<Badge variant="outline">Tools</Badge>
									)}
									{selectedModelInfo.supportsVision && (
										<Badge variant="outline">Vision</Badge>
									)}
								</div>
								{selectedModelInfo.description && (
									<p className="text-xs text-gray-500">{selectedModelInfo.description}</p>
								)}
							</div>
						)}

						{mode === 'text' ? (
							<>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Temperature: {temperature.toFixed(1)}
									</label>
									<input
										type="range"
										min="0"
										max="2"
										step="0.1"
										value={temperature}
										onChange={(e) => setTemperature(parseFloat(e.target.value))}
										className="w-full"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Max Output Tokens
									</label>
									<input
										type="number"
										min="1"
										max="8192"
										value={maxOutputTokens}
										onChange={(e) => setMaxOutputTokens(parseInt(e.target.value, 10))}
										className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
									/>
								</div>

								<div>
									<label className="flex items-center gap-2">
										<input
											type="checkbox"
											checked={jsonMode}
											onChange={(e) => setJsonMode(e.target.checked)}
											disabled={!selectedModelInfo?.supportsJsonMode}
											className="rounded border-gray-300"
										/>
										<span className="text-sm font-medium text-gray-700">JSON Mode</span>
									</label>
									{!selectedModelInfo?.supportsJsonMode && (
										<p className="text-xs text-gray-500 mt-1">
											This model does not support JSON mode
										</p>
									)}
								</div>
							</>
						) : (
							<>
								{/* Size (only if model has sizeOptions) */}
								{selectedImageModelDef?.sizeOptions && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Size
										</label>
										<Select
											options={selectedImageModelDef.sizeOptions.map(s => ({
												value: s.value,
												label: s.label,
											}))}
											value={imageSize || selectedImageModelDef.defaultSize || ''}
											onValueChange={setImageSize}
										/>
									</div>
								)}

								{/* Aspect Ratio (only if model has aspectRatioOptions) */}
								{selectedImageModelDef?.aspectRatioOptions && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Aspect Ratio
										</label>
										<Select
											options={selectedImageModelDef.aspectRatioOptions.map(r => ({
												value: r,
												label: r,
											}))}
											value={aspectRatio || selectedImageModelDef.defaultAspectRatio || ''}
											onValueChange={setAspectRatio}
										/>
									</div>
								)}

								{/* Model-specific settings */}
								{selectedImageModelDef?.settings.map(field => (
									<div key={field.key}>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											{field.label}
										</label>
										{renderSettingField(field)}
									</div>
								))}
							</>
						)}

						<Textarea
							label="Prompt"
							value={prompt}
							onChange={(e) => setPrompt(e.target.value)}
							placeholder="Enter your prompt here..."
							rows={10}
							className="font-mono text-sm"
						/>

						<Button
							onClick={handleSubmit}
							disabled={loading || !prompt.trim() || !selectedModel}
							className="w-full"
						>
							{loading ? (
								<>
									<Spinner className="mr-2" size="sm" />
									Generating...
								</>
							) : (
								mode === 'image' ? 'Generate Image' : 'Generate Response'
							)}
						</Button>

						{error && (
							<div className="p-3 bg-red-50 border border-red-200 rounded-md">
								<p className="text-sm text-red-600">{error}</p>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Output Panel */}
				<Card>
					<CardHeader>
						<CardTitle>Response</CardTitle>
					</CardHeader>
					<CardContent>
						{loading && (
							<div className="flex items-center justify-center py-12">
								<Spinner size="lg" />
							</div>
						)}

						{!loading && mode === 'text' && response && (
							<div className="space-y-4">
								<div className="p-4 bg-gray-50 rounded-md border">
									<pre className="whitespace-pre-wrap text-sm font-mono">
										{response.text}
									</pre>
								</div>

								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<div className="text-gray-600">Input Tokens</div>
										<div className="font-semibold">{response.usage.inputTokens.toLocaleString()}</div>
									</div>
									<div>
										<div className="text-gray-600">Output Tokens</div>
										<div className="font-semibold">{response.usage.outputTokens.toLocaleString()}</div>
									</div>
									<div>
										<div className="text-gray-600">Provider</div>
										<div className="font-semibold">{response.provider}</div>
									</div>
									<div>
										<div className="text-gray-600">Model</div>
										<div className="font-semibold">{response.model}</div>
									</div>
								</div>
							</div>
						)}

						{!loading && mode === 'image' && imageResponse && (
							<div className="space-y-4">
								<div className="rounded-md border overflow-hidden bg-gray-50">
									<img
										src={`data:image/png;base64,${imageResponse.imageData}`}
										alt="Generated image"
										className="w-full h-auto"
									/>
								</div>

								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<div className="text-gray-600">Provider</div>
										<div className="font-semibold">{imageResponse.provider}</div>
									</div>
									<div>
										<div className="text-gray-600">Model</div>
										<div className="font-semibold">{imageResponse.model}</div>
									</div>
								</div>

								{imageResponse.imageUrl && (
									<div className="text-sm">
										<a
											href={imageResponse.imageUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary-600 hover:underline"
										>
											Open original URL
										</a>
									</div>
								)}
							</div>
						)}

						{!loading && !response && !imageResponse && (
							<div className="text-center py-12 text-gray-500">
								<p>
									{mode === 'image'
										? 'Generated image will appear here after you generate an image.'
										: 'Response will appear here after you generate a response.'}
								</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	);
}
